import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { useReveal } from "../hooks/useReveal";
import { PlayCircle, Clock, Lock, CheckCircle2, CalendarClock, ArrowRight, Trophy } from "lucide-react";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
const lessonCount = (c) => (c.modules || []).reduce((n, m) => n + (m.lessons || []).length, 0);

function Reveal({ children, delay = 0 }) {
  const { ref, visible } = useReveal();
  return <div ref={ref} className={`reveal ${visible ? "is-visible" : ""}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [access, setAccess] = useState([]);
  const [progress, setProgress] = useState([]);

  useEffect(() => {
    api.listCourses().then(setCourses).catch(() => {});
    api.myAccess().then(setAccess).catch(() => {});
    api.myProgress().then(setProgress).catch(() => {});
  }, []);

  const rows = courses.map((c) => {
    const g = access.find((x) => x.courseId === c.id);
    const active = g && new Date(g.expiresAt) > new Date();
    const p = progress.find((x) => x.courseId === c.id);
    const total = lessonCount(c);
    const done = p ? p.completed.length : 0;
    const pct = total ? Math.round((done / total) * 100) : 0;
    return { course: c, active, expiresAt: g?.expiresAt, done, total, pct };
  });
  const activeCount = rows.filter((c) => c.active).length;

  return (
    <main className="pt-[72px] min-h-screen relative overflow-hidden">
      <div className="absolute -top-10 right-0 h-80 w-80 rounded-full bg-[#ff3798]/15 blob" />
      <div className="max-w-7xl mx-auto px-5 py-12 relative z-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="text-[#d6ff71] text-sm">Личный кабинет</p>
              <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">Привет, {user.name}!</h1>
            </div>
            <div className="flex gap-4">
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 px-5 py-3"><div className="text-2xl font-bold text-[#d6ff71]">{activeCount}</div><div className="text-xs text-white/50">активных курсов</div></div>
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 px-5 py-3"><div className="text-2xl font-bold">{courses.length}</div><div className="text-xs text-white/50">всего курсов</div></div>
            </div>
          </div>
        </Reveal>

        <h2 className="mt-12 text-xl font-bold">Мои курсы</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 mt-6">
          {rows.map(({ course, active, expiresAt, done, total, pct }, i) => (
            <Reveal key={course.id} delay={i * 100}>
              <div className={`rounded-3xl overflow-hidden border hover-lift ${active ? "border-[#d6ff71]/40 bg-white/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={course.cover} alt={course.title} className={`w-full h-full object-cover ${active ? "" : "grayscale opacity-60"}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  {active
                    ? <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-[#d6ff71] text-black px-3 py-1 text-xs font-semibold"><CheckCircle2 size={13} /> Доступ открыт</span>
                    : <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/60 border border-white/20 px-3 py-1 text-xs"><Lock size={13} /> Нет доступа</span>}
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold">{course.title}</h3>
                  <div className="mt-3 flex items-center gap-4 text-sm text-white/50">
                    <span className="flex items-center gap-1.5"><PlayCircle size={15} />{total} уроков</span>
                    <span className="flex items-center gap-1.5"><Clock size={15} />{course.duration}</span>
                  </div>
                  {active && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs mb-1.5"><span className="flex items-center gap-1 text-white/60"><Trophy size={13} className="text-[#d6ff71]" /> Прогресс</span><span className="text-[#d6ff71] font-semibold">{done}/{total} · {pct}%</span></div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#ff3798] to-[#d6ff71] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-white/40"><CalendarClock size={13} /> Доступ ещё {daysLeft(expiresAt)} дн.</div>
                    </div>
                  )}
                  <button onClick={() => navigate(`/course/${course.id}`)} className={`mt-5 w-full rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${active ? "bg-[#ff3798] hover:bg-[#ff5aac]" : "border border-white/15 text-white/60 hover:border-white/30"}`}>
                    {active ? <>{pct === 100 ? "Повторить" : done > 0 ? "Продолжить" : "Начать"} <ArrowRight size={16} /></> : "Посмотреть программу"}
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {activeCount === 0 && courses.length > 0 && (
          <div className="mt-10 rounded-3xl border border-[#ff3798]/30 bg-[#ff3798]/10 p-8 text-center">
            <Lock className="mx-auto text-[#ff3798]" />
            <h3 className="mt-3 text-xl font-bold">Доступ пока не открыт</h3>
            <p className="text-white/60 mt-2 max-w-md mx-auto">Администратор откроет вам доступ к курсам в ближайшее время.</p>
          </div>
        )}
      </div>
    </main>
  );
}
